import { serializedTypes } from '@/reports/index';
import { Stack, TabsNavigation, Tab } from '@/theme/components';
import { TabData } from '@/theme/components/TabsNavigation/TabsNavigation';
import { styled } from '@/theme/index';
import { useScreenSize } from '@/utils/useScreenSize';
import { filterRewards, rewardCategories } from './helpers';
import { BPReward, Reward } from './types';
import { PARTNER_REQUIRES_REPORT, filterRewardsByState } from './utils';
import ClickOutPartner from '../ClickOutPartner/index';
import { logoClassStyle } from '../ClickOutPartner/utils';
import { PartnerRedirect } from '../PartnerRedirect/PartnerRedirect';
import { InitialRecordSearch } from '../PerksPage';

interface RewardsProps {
  filterByCategories?: boolean;
  rewards: Reward[] | BPReward[];
  activeReward: string | Reward;
  hasSelectedReward: boolean;
  selectRewardPartner: (reward: Reward | string) => void;
  setCarwiserModalIsOpen?: (isClosed: boolean) => void;
  state?: string;
  usesFilterByState?: boolean;
  zipCode?: string;
  report?: serializedTypes.SerializedProperty;
  initialRecordSearch: InitialRecordSearch;
}

const CardsContainer = styled(Stack)(({ theme }) => ({
  gap: '12px',
  [theme.breakpoints.up('sm')]: {
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: '20px 12px',
  },
}));

// TODO: This will need a refactor to be more generic, currently these colors only works for Bumper
// But do to the limited pallet right now I'm going to leave it as is
const StyledTab = styled(Tab)(({ theme }) => ({
  color: '#8736C1',
  backgroundColor: '#8736C11F',
  fontSize: '1rem',
  fontWeight: 'bold',
  textDecoration: 'none',
  padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  marginRight: theme.spacing(1.5),
  border: 'none',
  borderRadius: theme.spacing(2.75),
  minWidth: 'unset',
  minHeight: 'unset',
  '.MuiTabs-root': {
    marginRight: 10,
  },

  '&.Mui-selected': {
    backgroundColor: '#8736C1',
    color: theme.palette.common.white,
  },
}));

export const Rewards = ({
  filterByCategories,
  rewards,
  activeReward,
  hasSelectedReward,
  selectRewardPartner,
  setCarwiserModalIsOpen,
  usesFilterByState = false,
  state,
  zipCode,
  report,
  initialRecordSearch,
}: RewardsProps) => {
  const { isMobile } = useScreenSize();
  const logoHeight = isMobile ? '80px' : '120px';
  const logoWidth = isMobile ? '80px' : '120px';

  const rewardComponentMap = (reward: Reward | BPReward) => {
    // Some Ownely Rewards are being filtered by state or zip code
    if (usesFilterByState && 'expanded' in reward && state && zipCode) {
      reward = filterRewardsByState(reward, state, zipCode);

      if (!reward) return null;
    }

    if (
      PARTNER_REQUIRES_REPORT.includes(reward.name) &&
      Object.values(initialRecordSearch).every((value) => value === '')
    ) {
      return null;
    }

    const components: Record<string, JSX.Element> = {
      'rewards/partner-redirect': (
        <PartnerRedirect
          key={reward.name}
          header={(reward as BPReward).header}
          partnerName={reward.name}
          logo={logoClassStyle(
            (reward as BPReward).image,
            logoHeight,
            logoWidth
          )}
          body={(reward as BPReward).body}
          cta={(reward as BPReward).cta}
          disclaimer={(reward as BPReward).disclaimer}
          promoCode={(reward as BPReward).promoCode}
          selectRewardPartner={() => {
            (reward as BPReward).name === 'carwiser' && setCarwiserModalIsOpen
              ? setCarwiserModalIsOpen(true)
              : window.open((reward as BPReward).link, '_blank');
          }}
        />
      ),
      'rewards/clickout-partner/widget': (
        <ClickOutPartner
          key={reward.name}
          reward={reward as Reward}
          partnerName={reward.name}
          selectRewardPartner={() => selectRewardPartner(reward as Reward)}
          currentRewardCollapsed={
            activeReward !== reward.name || hasSelectedReward
          }
          isFormReward={false}
          initialRecordSearch={initialRecordSearch}
        />
      ),
      'rewards/bubble/widget': (
        <ClickOutPartner
          key={reward.name}
          reward={reward as Reward}
          report={report}
          partnerName={reward.name}
          selectRewardPartner={() => selectRewardPartner(reward as Reward)}
          currentRewardCollapsed={
            activeReward !== reward.name || hasSelectedReward
          }
          isFormReward
          initialRecordSearch={initialRecordSearch}
        />
      ),
      'rewards/clever/widget': (
        <ClickOutPartner
          key={reward.name}
          reward={reward as Reward}
          report={report}
          partnerName={reward.name}
          selectRewardPartner={() => selectRewardPartner(reward as Reward)}
          currentRewardCollapsed={
            activeReward !== reward.name || hasSelectedReward
          }
          isFormReward
          initialRecordSearch={initialRecordSearch}
        />
      ),
      'rewards/morty/widget': (
        <ClickOutPartner
          key={reward.name}
          reward={reward as Reward}
          report={report}
          partnerName={reward.name}
          selectRewardPartner={() => selectRewardPartner(reward as Reward)}
          currentRewardCollapsed={
            activeReward !== reward.name || hasSelectedReward
          }
          isFormReward
          initialRecordSearch={initialRecordSearch}
        />
      ),
    };

    return components[reward.component] ?? null;
  };

  const renderCards = (rewards: BPReward[] | Reward[]) => (
    <CardsContainer>
      {rewards &&
        rewards.map((reward: Reward | BPReward) => rewardComponentMap(reward))}
    </CardsContainer>
  );

  if (!filterByCategories) {
    return renderCards(rewards);
  }
  const rewardsFilterByCategory: TabData[] = rewardCategories(
    rewards as BPReward[]
  ).map((category: string) => ({
    label: category,
    content: renderCards(filterRewards(category, rewards as BPReward[])),
  }));

  return (
    <TabsNavigation
      data-testid="rewards-categories"
      tabs={rewardsFilterByCategory}
      tab={StyledTab}
      sx={{
        mb: 1,
        '& .MuiTabs-indicator': { display: 'none' },
        '.MuiTabs-scrollButtons': { display: 'none' },
      }}
    />
  );
};
